import { ADHESION_DESKTOP_CSS_VAR, ADHESION_EVENT, ADHESION_MOBILE_CSS_VAR } from '@/defs/ad'
import {
  HEADER_HEIGHT_CSS_VAR,
  HEADER_HEIGHT_FALLBACK,
  HEIGHT_AVAILABLE_CSS_VAR,
  type HeightAvailableConfigType,
} from '@/defs/observer'

let HEIGHT_AVAILABLE: HeightAvailableConfigType | undefined

const measureHeaderHeight = () => {
  const probe = document.createElement('div')
  probe.style.cssText = `position:absolute;visibility:hidden;height:var(${HEADER_HEIGHT_CSS_VAR})`
  document.body.appendChild(probe)
  const { height } = probe.getBoundingClientRect()
  probe.remove()

  return height || HEADER_HEIGHT_FALLBACK
}

const buildHeightAvailableConfig = (): HeightAvailableConfigType => {
  const elements = new Set<HTMLElement>()
  let frame = 0
  let headerHeight = measureHeaderHeight()

  const update = () => {
    frame = 0

    const style = getComputedStyle(document.documentElement)
    const adhesionHeight =
      (parseFloat(style.getPropertyValue(ADHESION_DESKTOP_CSS_VAR)) || 0) +
      (parseFloat(style.getPropertyValue(ADHESION_MOBILE_CSS_VAR)) || 0)
    const viewportBottom = window.innerHeight - adhesionHeight

    const measured = [...elements].map((el) => {
      const { bottom, top } = el.getBoundingClientRect()
      const available = Math.min(viewportBottom, bottom) - Math.max(top, headerHeight)

      return { available: Math.floor(Math.max(0, available)), el }
    })

    measured.forEach(({ available, el }) => el.style.setProperty(HEIGHT_AVAILABLE_CSS_VAR, `${available}px`))
  }

  const schedule = () => {
    if (frame) return
    frame = requestAnimationFrame(update)
  }

  const onResize = () => {
    headerHeight = measureHeaderHeight()
    schedule()
  }

  const stop = () => {
    if (frame) cancelAnimationFrame(frame)
    observer.disconnect()
    window.removeEventListener('scroll', schedule)
    window.removeEventListener('resize', onResize)
    window.removeEventListener(ADHESION_EVENT, schedule)
    HEIGHT_AVAILABLE = undefined
  }

  const observer = new ResizeObserver(schedule)
  observer.observe(document.documentElement)
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', onResize)
  window.addEventListener(ADHESION_EVENT, schedule)

  return { elements, observer, schedule, stop }
}

export const getHeightAvailableConfig = () => {
  if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return
  if (!HEIGHT_AVAILABLE) HEIGHT_AVAILABLE = buildHeightAvailableConfig()

  return HEIGHT_AVAILABLE
}
